import React from "react";
import PropTypes from "prop-types";

// External Libraries
import { flexRender } from "@tanstack/react-table";

// Local Assets
import Spinner from "react-bootstrap/Spinner";
import ArrowDown from "../../assets/icons/arrow_down.svg";
import ArrowUp from "../../assets/icons/arrow_up.svg";
import BackArrow from "../../assets/icons/back-arrow.svg";
import ForwardArrow from "../../assets/icons/forward-arrow.svg";

// Helper Functions
import { optionalChaining } from "../../helper";

// Local Stylesheet
import "./GenericTable.scss";

// Constant
export const HAS_INDIVIDUAL_CLICK = "hasIndividualClick";

function GenericTable({
  table,
  pageIndex = 0,
  tableContainerStyle = {},
  tableClassName = "",
  shouldEnablePagination = false,
  isLoading = false,
  noRecordFoundMsg = "No record Found.",
  rowOnClickHandler = () => {},
  setPageIndex = () => {},
}) {
  const isNoOfPageIsMoreThanThree = table.getPageCount() > 3;
  const paginationSequence = generateSequence(
    pageIndex + 1,
    table.getPageCount()
  );

  const shouldShowLastOption =
    Math.max(...paginationSequence) !== table.getPageCount() &&
    isNoOfPageIsMoreThanThree;
  return (
    <div>
      <div
        className={`table-container ${tableClassName}`}
        style={tableContainerStyle}
      >
        <table className="custom-table">
          <thead className="thead">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : "",
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {
                          {
                            asc: <img src={ArrowUp} alt="up" />,
                            desc: <img src={ArrowDown} alt="down" />,
                          }[header.column.getIsSorted()]
                        }
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {!isLoading && (
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      onClick={() => {
                        const hasIndividualClick = optionalChaining(
                          cell,
                          ["column", "columnDef", "meta", "hasIndividualClick"],
                          false
                        );
                        if (!hasIndividualClick) {
                          rowOnClickHandler(row);
                        }
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
        {isLoading && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ padding: "200px" }}
          >
            <Spinner animation="border" role="status" />
          </div>
        )}
        {!isLoading && table.getRowModel().rows.length === 0 && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ padding: "200px" }}
          >
            <div className="w-100 font-size-20 text-color-020617 text-center">
              {noRecordFoundMsg}
            </div>
          </div>
        )}
      </div>
      {shouldEnablePagination &&
        !isLoading &&
        table.getRowModel().rows.length > 0 && (
          <div className="pagination-container">
            <button
              className="action-btn"
              disabled={!table.getCanPreviousPage()}
              onClick={() => setPageIndex(pageIndex - 1)}
            >
              <img src={BackArrow} alt="go-back" />
            </button>
            {!isNoOfPageIsMoreThanThree &&
              paginationSequence.map((pageNumber) => (
                <button
                  className={`page-button ${
                    pageNumber === pageIndex + 1 ? "page-button-active" : ""
                  }`}
                  onClick={() => setPageIndex(pageNumber - 1)}
                  key={pageNumber}
                >
                  {pageNumber}
                </button>
              ))}
            {isNoOfPageIsMoreThanThree &&
              paginationSequence.map((pageNumber) => (
                <button
                  className={`page-button ${
                    pageNumber === pageIndex + 1 ? "page-button-active" : ""
                  }`}
                  onClick={() => setPageIndex(pageNumber - 1)}
                  key={pageNumber}
                >
                  {pageNumber}
                </button>
              ))}
            {shouldShowLastOption && (
              <div style={{ color: "#020617", fontSize: "14px" }}>...</div>
            )}
            {shouldShowLastOption && (
              <button
                className="page-button"
                onClick={() => setPageIndex(table.getPageCount() - 1)}
              >
                {table.getPageCount()}
              </button>
            )}
            <button
              className="action-btn"
              disabled={!table.getCanNextPage()}
              onClick={() => setPageIndex(pageIndex + 1)}
            >
              <img src={ForwardArrow} alt="go-forward" />
            </button>
          </div>
        )}
    </div>
  );
}

GenericTable.propTypes = {
  table: PropTypes.object.isRequired,
  pageIndex: PropTypes.number,
  tableContainerStyle: PropTypes.object,
  tableClassName: PropTypes.string,
  shouldEnablePagination: PropTypes.bool,
  isLoading: PropTypes.bool,
  noRecordFoundMsg: PropTypes.string,
  rowOnClickHandler: PropTypes.func,
  setPageIndex: PropTypes.func,
};

export default GenericTable;

// Util function
/**
 * Generates a sequence of three consecutive integers based on the input value 'n' and a limit.
 *
 * @param {number} n - The input value used to generate the sequence.
 * @param {number} limit - The upper limit for the generated sequence.
 *
 * @returns {Array} An array containing three consecutive integers.
 */
function generateSequence(n, limit) {
  if (n < 3) {
    return [1, 2, 3].filter((e) => e <= limit);
  } else if (n === limit) {
    return generateSequence(limit - 1, limit);
  } else {
    const startValue = Math.floor((n - 1) / 2) * 2 + 1;
    return [startValue, startValue + 1, startValue + 2].filter(
      (e) => e <= limit
    );
  }
}
